import { datadogLogs } from "@datadog/browser-logs";
import { extractId } from "@repo/shopify/src/utils";
import type { MailingAddressInput } from "@shopify/hydrogen-react/storefront-api-types";
// import type { PaymentWidgetInstance } from "@tosspayments/payment-widget-sdk";
import { loadPaymentWidget } from "@tosspayments/payment-widget-sdk";
// import { PaymentMethodsWidget } from "@tosspayments/payment-widget__types";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCart from "@hooks/reducerHooks/useCart";
import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";

import { fetchPayment } from "@api/lamdaApi";

const PAYMENT_PARAMETER_SAVE_KEY = "ARCHIVE_SALE_paymentParameter";
const CLIENT_KEY = import.meta.env.VITE_REACT_APP_TOSS_CLIENT_KEY;

interface Props {
  payment?: any;
  paymentParameter?: any;
}

export interface IPaymentParam {
  amount: number;
  orderData: {
    shipping_lines: { price: number; title: string }[];
    discount_codes?: { code: string; amount: number }[];
    billing_address: MailingAddressInput;
    shipping_address: MailingAddressInput;
    line_items: {
      variant_id: string;
      quantity: number;
      applied_discounts?: { amount: number; description: string }[];
    }[];
    customer: { id: string };
    orderName: string;
  };
}

const usePayment = ({ payment, paymentParameter }: Props = {}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useLayout();
  const { accessToken, customer, fetchUser } = useUser();

  const [isLoading, setIsLoading] = useState(true); // 토스 페이먼츠 위젯 로딩중
  const [isProgressing, setIsProgressing] = useState(false); // 주문 생성 lambda API 진행중

  /** 토스 페이먼츠 결제 위젯 객체 저장 */
  const [widget, setWidget] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<any>(undefined);

  const { createCartWithParams } = useCart();

  /**
   * 토스 페이먼츠 결제 위젯 - 결제수단 UI 렌더링
   * https://docs.tosspayments.com/reference/widget-sdk#renderpaymentmethods%EC%84%A0%ED%83%9D%EC%9E%90-%EA%B2%B0%EC%A0%9C-%EA%B8%88%EC%95%A1
   */
  const loadPayment = async () => {
    if (widget) return;

    // 미로그인으로 체크아웃 페이지 진입 시 모달 노출
    // TODO: 오류 메세지
    if (!accessToken || !customer.phone || customer.tags.includes("Blocked")) return;

    // customerId is undefined 처리
    // TODO: 이전 페이지로 이동해야 하는지?
    if (!extractId(customer.id)) {
      openModal({
        title: "Payment Error",
        description: NOTIFICATIONS.WARN_UNDEFINED.message,
        confirmButton: {
          label: "Okay",
          onClick: () => {
            closeModal();
          },
        },
      });
      return;
    }

    try {
      if (
        window.location.pathname === URLS.ACCOUNT_DRAW_HISTORY ||
        window.location.pathname.startsWith(URLS.DRAW_ENTRY) ||
        window.location.pathname.startsWith(URLS.DRAW_ENTRY_DETAIL) ||
        document.getElementById("payment-method") === null
      ) {
        return;
      }

      await loadPaymentWidget(CLIENT_KEY as string, extractId(customer.id), {
        brandpay: {
          redirectUrl: "https://mrzkttj5xheyxkyizd7uilzthq0wptiz.lambda-url.ap-northeast-2.on.aws/", // tune_brandpay_token
        },
      })
        .then((paymentWidget) => {
          const paymentMethod = paymentWidget.renderPaymentMethods("#payment-method", 999999);
          // paymentWidget.renderAgreement("#agreement");
          return { paymentWidget, paymentMethod };
        })
        .then(({ paymentWidget, paymentMethod }) => {
          setWidget(paymentWidget);
          setPaymentMethod(paymentMethod);
        });
    } catch (error) {
      // openModal({
      //   title: "Payment Error",
      //   description: NOTIFICATIONS.ERROR_PAYMENT_SET.message,
      //   confirmButton: {
      //     label: "Okay",
      //     onClick: () => {
      //       closeModal();
      //     },
      //   },
      // });

      const message = error instanceof Error ? error.message : String(error);
      datadogLogs.logger.error("[TOSS] Failed to load", { message });
      throw new Error(`[TOSS] Failed to load : ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 토스 페이먼츠 결제 위젯 - 고객이 선택한 결제 수단의 결제창을 띄우는 메서드
   * https://docs.tosspayments.com/reference/widget-sdk#requestpayment%EA%B2%B0%EC%A0%9C-%EC%A0%95%EB%B3%B4
   */
  const requestPayment = useCallback(
    async ({ orderId: orderIdProp, orderName: orderNameProp, ...restParams }: any) => {
      const orderId = orderIdProp ?? `${extractId(customer.id)}_${Number(new Date())}`;
      const orderName = orderNameProp ?? paymentParameter.orderName;

      /** 토스 결제 요청했을 때의 paymentParameter을 sessionStorage 저장 */
      if (paymentParameter) {
        sessionStorage.setItem(PAYMENT_PARAMETER_SAVE_KEY, JSON.stringify(paymentParameter));
      }

      try {
        if (!widget) return;

        await widget
          .requestPayment({
            orderId,
            orderName,
            ...restParams,
            successUrl: window.location.href,
            failUrl: window.location.href,
            customerName: customer.email ?? "",
          })
          .catch((error: any) => {
            const message = error instanceof Error ? error.message : String(error);
            console.log("[TUNE] Payment Error : ", error.code, error);
            openModal({
              title: "Payment Error",
              description: message,
              confirmButton: {
                label: "Okay",
                onClick: () => {
                  closeModal();
                },
              },
            });
          });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        openModal({
          title: "Payment Error",
          description: message,
          confirmButton: {
            label: "Okay",
            onClick: () => {
              closeModal();
            },
          },
        });

        datadogLogs.logger.error("[TOSS] Failed to load widget", { message });
        throw new Error(`[TOSS] Failed to load widget : ${message}`);
      }
    },
    [widget, paymentParameter],
  );

  /** 주문 생성 lambda API */
  const postPayment = useCallback(
    async ({
      paymentKey,
      orderId,
      amount,
      brandPay,
      cartId,
    }: {
      paymentKey: string;
      orderId: string;
      amount: number;
      brandPay: string;
      cartId?: string;
    }) => {
      if (isProgressing) return;

      /** 토스 결제 요청했을 때 sessionStorage 저장된 paymentParameter */
      const paymentParameter = JSON.parse(sessionStorage.getItem(PAYMENT_PARAMETER_SAVE_KEY)!);
      if (!paymentParameter) {
        openModal({
          title: "Payment Error",
          description: NOTIFICATIONS.ERROR_PAYMENT_INFO.message,
          confirmButton: {
            label: "Okay",
            onClick: () => {
              closeModal();
            },
          },
        });
      }

      console.log("[TUNE] Log 'ORDER PAYMENT PARAM' right before calling fetchPayment", paymentParameter);

      setIsProgressing(true);

      try {
        const response = await fetchPayment({ cartId, paymentKey, orderId, amount, brandPay, ...paymentParameter });

        console.log("[TUNE] ORDER LAMBDA RESPONSE", response);

        if (response?.status === 200) {
          await createCartWithParams({
            variables: {
              input: {
                attributes: [{ key: "isFamilySale", value: "true" }],
                buyerIdentity: { customerAccessToken: accessToken },
              },
            },
          });

          await fetchUser({ variables: { customerAccessToken: accessToken } }).then(() => {
            navigate(`/order-completed/${response.orderId}`);
          });
        } else {
          openModal({
            title: "Payment Error",
            description: response?.message || NOTIFICATIONS.ERROR_PAYMENT_COMPLETE.message,
            confirmButton: {
              label: "Okay",
              onClick() {
                setSearchParams({});
                closeModal();
                navigate(URLS.HOME);
              },
            },
          });
        }
      } catch (error: any) {
        openModal({
          title: "Payment Error",
          description: NOTIFICATIONS.ERROR_PAYMENT_COMPLETE.message,
          confirmButton: {
            label: "Okay",
            onClick: () => {
              setSearchParams({});
              closeModal();
              navigate(URLS.HOME);
            },
          },
        });

        const message = error instanceof Error ? error.message : String(error);
        datadogLogs.logger.error("Something went wrong", { message });
        throw new Error(`Something went wrong : ${message}`);
      } finally {
        setIsProgressing(false);
        sessionStorage.removeItem(PAYMENT_PARAMETER_SAVE_KEY); // sessionStorage 저장된 paymentParameter 제거
      }
    },
    [isProgressing, navigate, createCartWithParams, openModal, setSearchParams, accessToken, fetchUser],
  );

  useEffect(() => {
    if (paymentMethod) {
      paymentMethod.updateAmount(payment?.totalAmount + payment?.shippingFee);
    }
  }, [payment]);

  useEffect(() => {
    setTimeout(() => {
      loadPayment().then();
    }, 300);
  }, []);

  return {
    isLoading,
    requestPayment,
    isProgressing,
    setIsProgressing,
    paymentMethod,
    postPayment,
    loadPayment,
    widget,
  };
};

export default usePayment;
